import { HighlightOff } from "@mui/icons-material";
import { Chip, Typography } from "@mui/material";
import { ReactElement, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { MessageClassDisplayName } from "~frontend/components/MessageClassDisplayName";
import {
  ALL_CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_FILTERS,
} from "~frontend/components/threads/constants";
import theme, { palette } from "~frontend/theme";

import { useThreadsContext } from "../../../../components/threads/ThreadsProvider";
import { ThreadResponseStatusLabel } from "../ThreadResponseStatusLabel";

export function ThreadListFilters(): ReactElement {
  const { listThreadsFilterAndSort, setListThreadFilterAndSort } =
    useThreadsContext()!;

  const {
    optOutFilter,
    responseStatusFilter,
    campaignFilter,
    campaignStatuses,
    texterFilter,
    tagFilter,
    search,
    phoneNumberMessageClassesFilter,
    inboxes,
  } = listThreadsFilterAndSort;

  if (
    campaignFilter.some(campaign => !campaign.name) ||
    texterFilter.some(texter => !texter.name) ||
    inboxes.some(inbox => !inbox.name)
  ) {
    return <Loading />;
  }

  const filterPills: ReactNode[] = [];

  if (campaignFilter.length > 2) {
    filterPills.push(
      <ThreadListFilterPill
        key={`campaign_multiple`}
        label={`(${campaignFilter.length}) Campaigns`}
        onClear={() => {
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            campaignFilter: [],
          });
        }}
      />
    );
  } else if (campaignFilter.length > 0) {
    filterPills.push(
      ...campaignFilter.map(campaign => (
        <ThreadListFilterPill
          key={`campaign_${campaign.id}`}
          label={campaign.name}
          onClear={() =>
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              campaignFilter: listThreadsFilterAndSort.campaignFilter.filter(
                c => c.id !== campaign.id
              ),
            })
          }
        />
      ))
    );
  }

  const hasAllCampaignStatusFilters = ALL_CAMPAIGN_STATUSES.every(sf =>
    campaignStatuses.includes(sf)
  );

  if (campaignStatuses.length > 0 && !hasAllCampaignStatusFilters) {
    filterPills.push(
      ...campaignStatuses.map(campaignStatus => (
        <ThreadListFilterPill
          key={`campaign_status_${campaignStatus}`}
          label={
            CAMPAIGN_STATUS_FILTERS.find(cs => cs.value === campaignStatus)!
              .pillLabel
          }
          onClear={() => {
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              campaignStatuses:
                listThreadsFilterAndSort.campaignStatuses.filter(
                  cs => cs !== campaignStatus
                ),
            });
          }}
        />
      ))
    );
  }

  if (tagFilter.tags.length > 0) {
    filterPills.push(
      tagFilter.tags.map(tag => (
        <ThreadListFilterPill
          key={`tag_${tag.id}`}
          label={tag.name}
          onClear={() => {
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              tagFilter: {
                tags: listThreadsFilterAndSort.tagFilter.tags.filter(
                  t => t.id !== tag.id
                ),
                filterOperation:
                  listThreadsFilterAndSort.tagFilter.filterOperation,
              },
            });
          }}
        />
      ))
    );
  }

  if (texterFilter.length > 2) {
    filterPills.push(
      <ThreadListFilterPill
        key={`texter_multiple`}
        label={`(${texterFilter.length}) Texters`}
        onClear={() => {
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            texterFilter: [],
          });
        }}
      />
    );
  } else if (texterFilter.length > 0) {
    filterPills.push(
      ...texterFilter.map(texter => (
        <ThreadListFilterPill
          key={`texter_${texter.id}`}
          label={texter.name}
          onClear={() =>
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              texterFilter: listThreadsFilterAndSort.texterFilter.filter(
                t => t.id !== texter.id
              ),
            })
          }
        />
      ))
    );
  }

  if (search.length > 0) {
    filterPills.push(
      <ThreadListFilterPill
        key="contactOrMessageSearchFilter"
        label={search}
        onClear={() =>
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            search: "",
          })
        }
      />
    );
  }

  if (responseStatusFilter) {
    filterPills.push(
      <ThreadListFilterPill
        key="needsTexterResponseFilter"
        label={
          <ThreadResponseStatusLabel responseStatus={responseStatusFilter} />
        }
        onClear={() =>
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            responseStatusFilter: null,
          })
        }
      />
    );
  }

  if (optOutFilter === "OPTED_OUT") {
    filterPills.push(
      <ThreadListFilterPill
        key="optedOut"
        label={
          <FormattedMessage
            defaultMessage="Opted Out"
            description="Label for opted out option"
          />
        }
        onClear={() =>
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            optOutFilter: "ANY",
          })
        }
      />
    );
  }

  if (optOutFilter === "NOT_OPTED_OUT") {
    filterPills.push(
      <ThreadListFilterPill
        key="optedIn"
        label={
          <FormattedMessage
            defaultMessage="Not Opted Out"
            description="Label for not-opted-out option"
          />
        }
        onClear={() =>
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            optOutFilter: "ANY",
          })
        }
      />
    );
  }

  if (phoneNumberMessageClassesFilter.length > 0) {
    filterPills.push(
      ...phoneNumberMessageClassesFilter.map(messageClass => (
        <ThreadListFilterPill
          key={`messageClass_${messageClass}`}
          label={<MessageClassDisplayName messageClass={messageClass} />}
          onClear={() =>
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              phoneNumberMessageClassesFilter:
                phoneNumberMessageClassesFilter.filter(m => m !== messageClass),
            })
          }
        />
      ))
    );
  }

  if (inboxes.length > 0) {
    filterPills.push(
      ...inboxes.map(inbox => (
        <ThreadListFilterPill
          key={`inbox_${inbox.id}`}
          label={inbox.name}
          onClear={() =>
            setListThreadFilterAndSort({
              ...listThreadsFilterAndSort,
              inboxes: inboxes.filter(i => i.id !== inbox.id),
            })
          }
        />
      ))
    );
  }

  if (filterPills.length === 0) {
    return <></>;
  }

  return (
    <FlexBox flexWrap="wrap" gap="8px" sx={{ paddingBottom: "20px" }}>
      {filterPills}
      <LinkButton
        onClick={() =>
          setListThreadFilterAndSort({
            ...listThreadsFilterAndSort,
            optOutFilter: "ANY",
            responseStatusFilter: null,
            campaignFilter: [],
            campaignStatuses: [],
            texterFilter: [],
            search: "",
            phoneNumberMessageClassesFilter: [],
            tagFilter: {
              tags: [],
              filterOperation:
                listThreadsFilterAndSort.tagFilter.filterOperation,
            },
            inboxes: [],
          })
        }
      >
        <Typography variant="body2" sx={{ paddingTop: theme.spacing(0.5) }}>
          <FormattedMessage
            defaultMessage="Clear Filters"
            description="Label for the clear filter button"
          />
        </Typography>
      </LinkButton>
    </FlexBox>
  );
}

function ThreadListFilterPill({
  label,
  onClear,
}: {
  label: ReactNode;
  onClear: () => void;
}): ReactElement {
  return (
    <Chip
      onClick={onClear}
      label={<Typography variant="body2">{label}</Typography>}
      icon={<HighlightOff fontSize="small" />}
      sx={{
        height: "21px",
        backgroundColor: palette.lightGray,
        color: palette.darkGray,
      }}
    />
  );
}
